import { useState, type KeyboardEvent } from 'react';
import { cn } from '@/shared/cn';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { TrendChart, type VizTone } from '@/versions/v1/modules/_viz';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { sparklineYDomain, type FunctionHealthCard, type FunctionHealthId, type FunctionHealthRead } from './FunctionHealth.transform';
import type { ByAreaRow, PerformanceViewModel } from './Performance.transform';
import { PerformanceByAreaTable } from './PerformanceTable';
import { PlanAchievementPanel } from './PlanAchievementPanel';
import { DashboardPanel } from './RoleLandingPanels';

function lightTone(light: FunctionHealthCard['light']): VizTone {
  if (light === 'ok') return 'ok';
  if (light === 'warn') return 'warn';
  return 'bad';
}

function pillTone(light: FunctionHealthCard['light']): 'success' | 'warning' | 'danger' {
  if (light === 'ok') return 'success';
  if (light === 'warn') return 'warning';
  return 'danger';
}

function EquipmentHealthTable({ rows }: { rows: readonly { id: string; asset: string; healthPct: number }[] }) {
  return (
    <Table className="text-[11px]">
      <THead>
        <TR>
          <TH className="py-1">Asset</TH>
          <TH className="py-1">Health</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.id}>
            <TD className="py-1 font-medium">{row.asset}</TD>
            <TD className="py-1 tabular">{row.healthPct}%</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function FunctionCardBody({
  card,
  byAreaRows,
  planAchievement,
  byAreaBasisLabel,
}: {
  card: FunctionHealthCard;
  byAreaRows: readonly ByAreaRow[];
  planAchievement?: PerformanceViewModel['planAchievement'];
  byAreaBasisLabel?: string;
}) {
  const filtered = byAreaRows.filter((row) => card.byAreaRowIds.includes(row.id));
  return (
    <div className="space-y-3 rounded-lg border border-[var(--hairline)] bg-[var(--surface-1)] p-3">
      {card.showPlanAchievement && planAchievement ? (
        <PlanAchievementPanel plan={planAchievement} />
      ) : null}
      {filtered.length > 0 ? (
        <PerformanceByAreaTable
          rows={filtered}
          weeklyProvenance="[syn]"
          mtdProvenance="[syn]"
          basisLabel={byAreaBasisLabel}
        />
      ) : null}
      {card.equipmentHealthRows && card.equipmentHealthRows.length > 0 ? (
        <DashboardPanel title="Equipment health index">
          <EquipmentHealthTable rows={card.equipmentHealthRows} />
        </DashboardPanel>
      ) : null}
    </div>
  );
}

/** `grid`: three separate cards. `joined`: one card, cells split by hairlines
 * (GM comp `.fhrow`). `stack`: separate cards in a column (GM comp `.rail2
 * .stack`). Both GM layouts draw the trend under the figure at the cell's own
 * width: TrendChart never draws narrower than 240px, so a comp-width 160px
 * column beside the figure would shrink its 10px ticks to about 7px. */
export type FunctionHealthLayout = 'grid' | 'joined' | 'stack';

function HealthSparkline({ card, height }: { card: FunctionHealthCard; height: number }) {
  return (
    <TrendChart
      kind="line"
      categories={card.sparkline.categories}
      series={[
        {
          name: card.label,
          points: card.sparkline.points,
          tone: lightTone(card.light),
        },
      ]}
      references={card.sparkline.references}
      height={height}
      yDomain={sparklineYDomain(card.sparkline)}
      referenceLabelPlacement="clear"
    />
  );
}

function FunctionHealthCardView({
  card,
  isOpen,
  onToggle,
  layout,
}: {
  card: FunctionHealthCard;
  isOpen: boolean;
  onToggle: () => void;
  layout: FunctionHealthLayout;
}) {
  const interaction = {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': isOpen,
    onClick: onToggle,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
  } as const;
  const toggle = (
    <button
      type="button"
      className="shrink-0 text-muted"
      aria-label={isOpen ? `Collapse ${card.label} detail` : `Expand ${card.label} detail`}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );

  if (layout !== 'grid') {
    const joined = layout === 'joined';
    return (
      <div
        {...interaction}
        data-function-health-card={card.id}
        className={cn(
          'grid min-w-0 cursor-pointer grid-cols-1 content-start items-start gap-y-1 px-3.5 pb-2 pt-2.5 transition-colors hover:bg-[var(--surface-1)]',
          joined
            ? 'border-b border-[var(--hairline)] last:border-b-0 lg:border-b-0 lg:border-r lg:last:border-r-0'
            : 'card-matte-2 rounded-2xl',
        )}
      >
        <div className="col-span-full flex min-w-0 items-center gap-2">
          <Pill tone={pillTone(card.light)} className="shrink-0 px-2 py-0 text-[10px] font-semibold">
            {card.light === 'ok' ? 'OK' : card.light === 'warn' ? 'Watch' : 'Alert'}
          </Pill>
          <p className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{card.label}</p>
          {card.provenanceChip ? (
            <ProvenanceChip text={card.provenanceChip} title={card.provenanceTitle} compact />
          ) : null}
          <span className="flex-1" />
          {toggle}
        </div>
        <div className="min-w-0">
          <p className="whitespace-nowrap text-[19px] font-semibold leading-tight tabular-nums text-ink-primary">{card.headline}</p>
          <p className="text-xs leading-snug text-muted">{card.reason}</p>
          {card.detailLine ? (
            <p className="mt-0.5 text-xs leading-snug text-[var(--state-warning)]">{card.detailLine}</p>
          ) : null}
        </div>
        <div className="mt-1 min-w-0">
          <HealthSparkline card={card} height={52} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[var(--hairline)] bg-[var(--surface-1)] p-2" {...interaction}>
      <div className="flex items-start gap-3">
        <Pill tone={pillTone(card.light)} className="mt-0.5 shrink-0 px-2 py-0.5 text-[10px]">
          {card.light === 'ok' ? 'OK' : card.light === 'warn' ? 'Watch' : 'Alert'}
        </Pill>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-xs font-medium uppercase tracking-wide text-muted">{card.label}</p>
            {card.provenanceChip ? (
              <ProvenanceChip text={card.provenanceChip} title={card.provenanceTitle} compact />
            ) : null}
          </div>
          <p className="whitespace-nowrap text-lg font-semibold tabular text-zinc-900 dark:text-zinc-100">{card.headline}</p>
          <p className="mt-0.5 text-xs text-muted">{card.reason}</p>
          {card.detailLine ? (
            <p className="mt-1 text-xs font-medium text-[var(--state-warning)]">{card.detailLine}</p>
          ) : null}
          <TrendChart
            kind="line"
            categories={card.sparkline.categories}
            series={[
              {
                name: card.label,
                points: card.sparkline.points,
                tone: lightTone(card.light),
              },
            ]}
            references={card.sparkline.references}
            height={36}
            className="mt-1"
          />
        </div>
        {toggle}
      </div>
    </div>
  );
}

export function FunctionHealthStrip({
  read,
  byAreaRows,
  planAchievement,
  byAreaBasisLabel,
  layout = 'grid',
}: {
  read: FunctionHealthRead;
  byAreaRows: readonly ByAreaRow[];
  planAchievement?: PerformanceViewModel['planAchievement'];
  byAreaBasisLabel?: string;
  layout?: FunctionHealthLayout;
}) {
  const [openId, setOpenId] = useState<FunctionHealthId | null>(null);
  const openCard = read.cards.find((card) => card.id === openId);

  return (
    <div className="space-y-3" data-function-health={layout}>
      <div
        className={cn(
          layout === 'joined'
            ? 'card-matte-2 grid grid-cols-1 overflow-hidden rounded-2xl lg:grid-cols-3'
            : layout === 'stack'
              ? 'flex flex-col gap-3'
              : 'grid grid-cols-1 gap-3 lg:grid-cols-3',
        )}
      >
        {read.cards.map((card) => (
          <FunctionHealthCardView
            key={card.id}
            card={card}
            layout={layout}
            isOpen={openId === card.id}
            onToggle={() => setOpenId((current) => (current === card.id ? null : card.id))}
          />
        ))}
      </div>
      {openCard ? (
        <FunctionCardBody
          card={openCard}
          byAreaRows={byAreaRows}
          planAchievement={planAchievement}
          byAreaBasisLabel={byAreaBasisLabel}
        />
      ) : null}
    </div>
  );
}
